import { useCallback, useState } from 'react';
import { policyDocumentsApi } from '@client/api/waterlily';
import { useToast } from '@client/components/hooks/use-toast';

/**
 * Opens an uploaded policy document (PDF) inline in a new tab.
 *
 * The signed URL is fetched first (callers show a loading state via `isViewing`),
 * and only on success is the tab opened. If the browser blocks the deferred
 * pop-up we surface a toast telling the user to allow pop-ups. Errors surface via
 * toast so both the funding-table action and the modal button get feedback.
 */
export function useViewPolicyDocument(clientId: string | undefined) {
  const { toast } = useToast();
  const [inFlight, setInFlight] = useState<Set<string>>(new Set());

  const viewDocument = useCallback(
    async (requestId: string) => {
      if (!clientId || !requestId) return;

      setInFlight(prev => new Set(prev).add(requestId));

      try {
        // `fileName` is intentionally unused: the inline view relies on S3's
        // response-content-disposition to name the document.
        const { url } = await policyDocumentsApi.getDownloadUrl(clientId, requestId);
        // Open only after the URL is ready. A deferred open can be pop-up
        // blocked; detect the null return and tell the user how to allow it.
        const tab = window.open(url, '_blank', 'noopener,noreferrer');
        if (!tab) {
          toast({
            title: 'Pop-up blocked',
            description: 'Allow pop-ups for this site to open the document.',
          });
        }
      } catch {
        toast({
          variant: 'destructive',
          title: "Couldn't open document",
          description: 'Something went wrong opening this document. Please try again.',
        });
      } finally {
        setInFlight(prev => {
          const next = new Set(prev);
          next.delete(requestId);
          return next;
        });
      }
    },
    [clientId, toast],
  );

  return {
    viewDocument,
    isViewing: (requestId: string | undefined | null) => requestId ? inFlight.has(requestId) : false,
  };
}
